import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type ItineraryComponentOut } from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { Car, GripVertical, Ticket } from 'lucide-react';
import React from 'react';

import { ComponentCard } from './ComponentCard';

/**
 * Day items - activities + internal transfers for a single day.
 *
 * Flat ordered list (no morning/afternoon/evening grouping). Default order is
 * the component's saved `sortOrder`; the operator can drag-to-reorder, and the
 * grip lives on each row. Two buttons at the bottom open the picker on the
 * activity or transfer tab respectively. The picker scopes to the day's city
 * by default.
 */

interface DaySlotsProps {
  components: ItineraryComponentOut[];
  editable: boolean;
  /**
   * Fixed departures ship a fixed day plan: the activities and transfers are
   * whatever the template says, and the land price already covers them. Adding
   * or removing here would make the itinerary stop matching what was quoted.
   */
  structureLocked?: boolean;
  onAdd: (kind: 'ACTIVITY' | 'TRANSFER') => void;
  onEdit: (c: ItineraryComponentOut) => void;
  onRemove: (c: ItineraryComponentOut) => void;
  /** Persist the new visual order to the draft. */
  onReorder: (orderedIds: string[]) => void;
  /** True when the itinerary's destination is LONG_HAUL. Enables the
   *  inline "Save net price" editor on any activity/transfer row whose
   *  `costPaise` is 0 (i.e. the master carries a zero rate). */
  isLongHaul?: boolean;
  /** Persist a manually-entered net price on the given component. Only
   *  invoked from the inline editor for long-haul zero-cost rows. */
  onManualPriceSave?: (componentId: string, newCostPaise: string) => void | Promise<void>;
  /** This day's calendar date (ISO YYYY-MM-DD). Passed through to the
   *  transfer card so a multi-day pass can show its validity window. */
  dayDate?: string | null;
}

export const DaySlots = ({
  components,
  editable,
  structureLocked = false,
  onAdd,
  onEdit,
  onRemove,
  onReorder,
  isLongHaul = false,
  onManualPriceSave,
  dayDate,
}: DaySlotsProps): React.JSX.Element => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Stable id list for SortableContext. Order matches the (already-sorted)
  // input - the parent supplies components in their authoritative order.
  const ids = components.map((c) => c.id);

  const handleDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const fromIdx = ids.indexOf(String(active.id));
    const toIdx = ids.indexOf(String(over.id));
    if (fromIdx < 0 || toIdx < 0) return;
    onReorder(arrayMove(ids, fromIdx, toIdx));
  };

  return (
    <div className="space-y-2.5">
      {components.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-200 px-3 py-4 text-center text-xs text-zinc-400">
          No activities or transfers yet. Use the buttons below to add some.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {components.map((c) => (
                <SortableRow
                  key={c.id}
                  component={c}
                  editable={editable}
                  structureLocked={structureLocked}
                  onEdit={() => onEdit(c)}
                  onRemove={() => onRemove(c)}
                  isLongHaul={isLongHaul}
                  onManualPriceSave={onManualPriceSave}
                  dayDate={dayDate}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {editable && !structureLocked && (
        <div className="grid gap-2 sm:grid-cols-2">
          <UzButton
            variant="ghost"
            size="sm"
            className="text-primary h-8 w-full justify-center text-xs"
            onClick={() => onAdd('ACTIVITY')}
          >
            <Ticket className="mr-1 h-3.5 w-3.5" aria-hidden />
            Add activity
          </UzButton>
          <UzButton
            variant="ghost"
            size="sm"
            className="text-primary h-8 w-full justify-center text-xs"
            onClick={() => onAdd('TRANSFER')}
          >
            <Car className="mr-1 h-3.5 w-3.5" aria-hidden />
            Add transfer
          </UzButton>
        </div>
      )}
    </div>
  );
};

const SortableRow = ({
  component,
  editable,
  structureLocked,
  onEdit,
  onRemove,
  isLongHaul,
  onManualPriceSave,
  dayDate,
}: {
  component: ItineraryComponentOut;
  editable: boolean;
  structureLocked: boolean;
  onEdit: () => void;
  onRemove: () => void;
  isLongHaul: boolean;
  onManualPriceSave?: (componentId: string, newCostPaise: string) => void | Promise<void>;
  dayDate?: string | null;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: component.id,
    disabled: !editable || structureLocked,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={'flex items-stretch gap-2 ' + (isDragging ? 'z-10 opacity-60 shadow-md' : '')}
    >
      {editable && !structureLocked && (
        <button
          type="button"
          className="cursor-grab self-stretch rounded-md px-1 text-zinc-300 hover:bg-zinc-50 hover:text-zinc-600 active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <ComponentCard
          component={component}
          editable={editable}
          onEdit={onEdit}
          onRemove={onRemove}
          isLongHaul={isLongHaul}
          onManualPriceSave={onManualPriceSave}
          dayDate={dayDate}
        />
      </div>
    </div>
  );
};
